import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { 
  ChevronRight, 
  CheckCircle2, 
  AlertTriangle, 
  Plus,
  Wrench,
  ListChecks,
  ArrowRight,
  Loader2,
  Package,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * JobGuidePanel - Unified "Job Guide & Suggested Items" panel
 * Uses the Canonical Rule Engine for deterministic suggestions
 */
const JobGuidePanel = ({ 
  jobGroup, 
  yestimateId,
  onPricingUpdate,
  appliedRules = [],
  onAddPart,
  onAddLabour
}) => {
  const [ruleData, setRuleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState({});
  const [showSideModal, setShowSideModal] = useState(false);
  const [pendingSideSelection, setPendingSideSelection] = useState(null);
  const [serviceMapping, setServiceMapping] = useState(null); // For service interval items
  const [addedParts, setAddedParts] = useState({}); // Track which parts have been added
  const [addedLabour, setAddedLabour] = useState({}); // Track which labour has been added
  const [autoApplied, setAutoApplied] = useState({}); // Track auto-applied items to prevent re-fire

  // Fetch rule data when job group changes
  useEffect(() => {
    // DEBUG: Log what we receive
    console.log('JobGuidePanel received jobGroup:', {
      id: jobGroup?.id,
      title: jobGroup?.title,
      vi_item_key: jobGroup?.vi_item_key,
      canonical_rule_id: jobGroup?.canonical_rule_id,
      is_interval_item: jobGroup?.is_interval_item,
      interval_item_id: jobGroup?.interval_item_id
    });
    
    // Check if this is a service interval item - fetch from service mappings instead
    if (jobGroup?.is_interval_item || jobGroup?.source === 'interval') {
      fetchServiceMapping();
      return;
    }
    
    // Try to derive vi_item_key from title if not set
    let itemKey = jobGroup?.vi_item_key;
    if (!itemKey && jobGroup?.title) {
      // Common title-to-key mappings for Job Guide
      const titleLower = jobGroup.title.toLowerCase();
      
      // Brake items
      if (titleLower.includes('front brake pad') || titleLower.includes('front pads')) {
        itemKey = 'front_brake_pads';
      } else if (titleLower.includes('rear brake pad') || titleLower.includes('rear pads')) {
        itemKey = 'rear_brake_pads';
      } else if ((titleLower.includes('front') && titleLower.includes('disc') && titleLower.includes('pad')) || 
                 titleLower.includes('front brakes')) {
        itemKey = 'front_brake_discs_pads';
      } else if ((titleLower.includes('rear') && titleLower.includes('disc') && titleLower.includes('pad')) || 
                 titleLower.includes('rear brakes')) {
        itemKey = 'rear_brake_discs_pads';
      } else if (titleLower.includes('front brake disc') || (titleLower.includes('front') && titleLower.includes('disc') && !titleLower.includes('pad'))) {
        itemKey = 'front_brake_discs';
      } else if (titleLower.includes('rear brake disc') || (titleLower.includes('rear') && titleLower.includes('disc') && !titleLower.includes('pad'))) {
        itemKey = 'rear_brake_discs';
      }
      // Tyre items
      else if (titleLower.includes('tyre') || titleLower.includes('tire')) {
        if (titleLower.includes('front') && (titleLower.includes('right') || titleLower.includes('os') || titleLower.includes('offside'))) {
          itemKey = 'tyre_front_os';
        } else if (titleLower.includes('front') && (titleLower.includes('left') || titleLower.includes('ns') || titleLower.includes('nearside'))) {
          itemKey = 'tyre_front_ns';
        } else if (titleLower.includes('rear') && (titleLower.includes('right') || titleLower.includes('os') || titleLower.includes('offside'))) {
          itemKey = 'tyre_rear_os';
        } else if (titleLower.includes('rear') && (titleLower.includes('left') || titleLower.includes('ns') || titleLower.includes('nearside'))) {
          itemKey = 'tyre_rear_ns';
        } else if (titleLower.includes('front')) {
          itemKey = 'tyre_front_ns';
        } else if (titleLower.includes('rear')) {
          itemKey = 'tyre_rear_ns';
        }
      }
      // Suspension items
      else if (titleLower.includes('shock absorber') || titleLower.includes('damper')) {
        itemKey = titleLower.includes('front') ? 'front_shock_absorbers' : 
                  titleLower.includes('rear') ? 'rear_shock_absorbers' : 'front_shock_absorbers';
      } else if (titleLower.includes('spring')) {
        itemKey = titleLower.includes('front') ? 'front_springs' : 
                  titleLower.includes('rear') ? 'rear_springs' : 'front_springs';
      } else if (titleLower.includes('track rod') || titleLower.includes('tie rod')) {
        itemKey = 'track_rod_ends';
      } else if (titleLower.includes('ball joint')) {
        itemKey = 'ball_joints';
      } else if (titleLower.includes('cv joint') || titleLower.includes('cv boot')) {
        itemKey = 'cv_joints_boots';
      } else if (titleLower.includes('wheel bearing')) {
        itemKey = 'wheel_bearings';
      } else if (titleLower.includes('anti-roll') || titleLower.includes('drop link')) {
        itemKey = titleLower.includes('front') ? 'front_arb_links' : 
                  titleLower.includes('rear') ? 'rear_arb_links' : 'front_arb_links';
      }
      // Service items
      else if (titleLower.includes('oil') && titleLower.includes('filter')) {
        itemKey = 'oil_filter';
      } else if (titleLower.includes('air filter')) {
        itemKey = 'air_filter';
      } else if (titleLower.includes('cabin filter') || titleLower.includes('pollen filter')) {
        itemKey = 'cabin_filter';
      } else if (titleLower.includes('wiper blade') || titleLower.includes('wiper')) {
        itemKey = titleLower.includes('front') ? 'front_wiper_blades' : 
                  titleLower.includes('rear') ? 'rear_wiper_blade' : 'front_wiper_blades';
      } else if (titleLower.includes('battery')) {
        itemKey = 'battery';
      }
      // Lighting
      else if (titleLower.includes('headlight') || titleLower.includes('headlamp') || titleLower.includes('dipped beam') || titleLower.includes('main beam')) {
        itemKey = 'headlight_bulbs';
      } else if (titleLower.includes('brake light') || titleLower.includes('stop light')) {
        itemKey = 'brake_light_bulbs';
      }
      // Dashboard warnings
      else if (titleLower.includes('engine warning') || titleLower.includes('eml') || titleLower.includes('check engine')) {
        itemKey = 'dash_warning_lamps';
      } else if (titleLower.includes('abs warning') || titleLower.includes('abs light')) {
        itemKey = 'abs_warning';
      } else if (titleLower.includes('airbag') || titleLower.includes('srs')) {
        itemKey = 'airbag_warning';
      }
      
      if (itemKey) {
        console.log(`JobGuidePanel: Derived vi_item_key '${itemKey}' from title '${jobGroup.title}'`);
      }
    }
    
    if (itemKey || jobGroup?.canonical_rule_id) {
      fetchRuleData(itemKey);
    } else {
      console.log('JobGuidePanel: No vi_item_key or canonical_rule_id, skipping fetch');
      setLoading(false);
      setRuleData(null);
    }
  }, [jobGroup?.id, jobGroup?.vi_item_key, jobGroup?.canonical_rule_id, jobGroup?.title, jobGroup?.is_interval_item, jobGroup?.interval_item_id, jobGroup?.source]);

  // Fetch service mapping for service interval items
  const fetchServiceMapping = async () => {
    setLoading(true);
    setRuleData(null);
    try {
      const serviceItemId = jobGroup.interval_item_id || 'oil_filter_service';
      console.log(`JobGuidePanel: Fetching service mapping for '${serviceItemId}'`);
      const res = await axios.get(`${API}/service-mappings/${serviceItemId}`);
      console.log('JobGuidePanel: Service mapping data:', res.data);
      setServiceMapping(res.data);
    } catch (err) {
      console.error('Failed to fetch service mapping:', err);
      setServiceMapping(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchRuleData = async (derivedItemKey = null) => {
    setLoading(true);
    try {
      const itemKey = derivedItemKey || jobGroup.vi_item_key;
      const canonicalRuleId = jobGroup.canonical_rule_id;

      // iter183 — prefer vi_item_key resolution. The MOT auto-quote
      // pipeline stamps canonical_rule_id with the *auto-applied child*
      // (e.g. rule_brake_fitting_kit_front for a pads JG), which has no
      // sibling relationships of its own → suggestion buckets come back
      // empty and FOH sees no Recommended chips.
      //
      // vi_item_key (e.g. "front_brake_pads") resolves through the seed
      // mapping table → always lands on the parent rule that carries
      // the full relationships.suggested list.
      //
      // Fallback ladder:
      //   1. vi_item_key → /api/rules/resolve/{key}  (preferred)
      //   2. if (1) failed AND canonical_rule_id present →
      //      /api/rules/{id}/suggestions  (legacy path)
      //   3. nothing matched → setRuleData(null)
      let resolved = null;
      if (itemKey) {
        try {
          const resolveRes = await axios.get(`${API}/api/rules/resolve/${itemKey}`);
          const hasAny =
            (resolveRes.data?.suggestions?.required || []).length > 0 ||
            (resolveRes.data?.suggestions?.suggested || []).length > 0 ||
            (resolveRes.data?.suggestions?.conditional || []).length > 0;
          if (resolveRes.data && hasAny) {
            resolved = resolveRes.data;
          }
        } catch {
          // Non-fatal — fall through to canonical fallback.
        }
      }
      if (!resolved && canonicalRuleId) {
        const suggestionsRes = await axios.get(
          `${API}/api/rules/${canonicalRuleId}/suggestions`,
        );
        const ruleRes = await axios.get(`${API}/api/rules/${canonicalRuleId}`);
        resolved = {
          rule: ruleRes.data,
          suggestions: suggestionsRes.data?.suggestions,
        };
      }
      setRuleData(resolved);
    } catch (err) {
      console.error("Failed to fetch rule data:", err);
      setRuleData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-apply items with auto_apply: true when rule data loads
  useEffect(() => {
    if (!ruleData?.suggestions) return;
    if (!yestimateId || !jobGroup?.id) return;

    const allSuggestions = [
      ...(ruleData.suggestions.required || []),
      ...(ruleData.suggestions.suggested || []),
      ...(ruleData.suggestions.conditional || [])
    ];

    const toAutoApply = allSuggestions.filter(item => 
      item.auto_apply && 
      item.priceable &&
      !isRuleApplied(item.rule_id) &&
      !autoApplied[item.rule_id] &&
      !item.requires_side_selection
    );

    if (toAutoApply.length === 0) return;

    // Mark as auto-applied immediately to prevent re-fire
    const newAutoApplied = {};
    toAutoApply.forEach(item => { newAutoApplied[item.rule_id] = true; });
    setAutoApplied(prev => ({ ...prev, ...newAutoApplied }));

    // Apply each item sequentially (silently - no toasts)
    const applyAll = async () => {
      for (const item of toAutoApply) {
        try {
          await applyRuleToJob(item.rule_id, null, true);
        } catch (err) {
          console.error(`Auto-apply failed for ${item.rule_id}:`, err);
        }
      }
    };
    applyAll();
  }, [ruleData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyRule = async (targetRuleId, requiresSide = false, sideOptions = []) => {
    // If side selection required, show modal
    if (requiresSide && sideOptions.length > 0) {
      setPendingSideSelection({ ruleId: targetRuleId, sideOptions });
      setShowSideModal(true);
      return;
    }

    await applyRuleToJob(targetRuleId, null);
  };

  const applyRuleToJob = async (ruleId, selectedSide, silent = false) => {
    setApplying(prev => ({ ...prev, [ruleId]: true }));
    
    try {
      const response = await axios.post(`${API}/api/rules/apply`, {
        yestimate_id: yestimateId,
        job_group_id: jobGroup.id,
        rule_id: ruleId,
        selected_side: selectedSide,
        source: silent ? 'AUTO_APPLY' : 'RULE_LIBRARY'
      });

      if (response.data.success) {
        if (response.data.lines_added > 0) {
          if (!silent) toast.success(response.data.message);
          // Notify parent to refresh pricing
          if (onPricingUpdate) {
            onPricingUpdate(jobGroup.id, response.data.added_lines);
          }
        } else if (response.data.lines_deduped > 0) {
          if (!silent) toast.info('Already added', { description: response.data.message });
        }
      } else {
        if (!silent) toast.error(response.data.message);
      }
    } catch (err) {
      if (!silent) toast.error('Failed to apply', { description: err.response?.data?.detail || err.message });
    } finally {
      setApplying(prev => ({ ...prev, [ruleId]: false }));
      setShowSideModal(false);
      setPendingSideSelection(null);
    }
  };

  const handleSideSelect = (side) => {
    if (pendingSideSelection) {
      applyRuleToJob(pendingSideSelection.ruleId, side);
    }
  };

  // Check if a rule has been applied
  const isRuleApplied = (ruleId) => {
    return appliedRules.includes(ruleId) || 
           jobGroup?.applied_rules?.includes(ruleId);
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading job guide...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render service mapping content for service interval items
  if (serviceMapping) {
    const isGeneric = serviceMapping.mapping_type === 'generic';
    return (
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white" data-testid={`job-guide-panel-${jobGroup.id}`}>
        <CardHeader className="py-3 px-4 border-b border-emerald-100">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-emerald-600" />
            <CardTitle className="text-sm font-semibold text-emerald-900">
              Job Guide & Suggested Items
            </CardTitle>
            {isGeneric && (
              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                Generic Default
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-4 space-y-4">
          {/* Job Guide Steps from Service Mapping */}
          {serviceMapping.job_guide && serviceMapping.job_guide.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <ListChecks className="w-4 h-4 text-slate-600" />
                <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">Advisor Guide</span>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <ol className="space-y-2">
                  {serviceMapping.job_guide.sort((a, b) => a.order - b.order).map((step, idx) => (
                    <li 
                      key={idx} 
                      className={`flex items-start gap-2 text-sm ${step.is_warning ? 'text-amber-700' : 'text-slate-700'}`}
                    >
                      <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                        step.is_warning ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {step.is_warning ? '!' : idx + 1}
                      </span>
                      <span>{step.text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Suggested Parts from Service Mapping */}
          {serviceMapping.suggested_parts && serviceMapping.suggested_parts.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-slate-600" />
                <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">Suggested Parts</span>
              </div>
              <div className="space-y-1.5">
                {serviceMapping.suggested_parts.map((part, idx) => {
                  const partKey = `part-${idx}`;
                  // Check if already added via local state OR if already exists in job parts_lines
                  const existingParts = jobGroup?.parts_lines || [];
                  const partNameLower = part.name?.toLowerCase().trim() || '';
                  const alreadyInJob = existingParts.some(p => {
                    const desc = p.description?.toLowerCase().trim() || '';
                    // Check for exact match or one contains the other
                    if (desc === partNameLower) return true;
                    if (desc.includes(partNameLower) || partNameLower.includes(desc)) return true;
                    // Check for fitting kit matches - must both be fitting kits
                    if (partNameLower.includes('fitting kit') && desc.includes('fitting kit')) return true;
                    // Check for brake pads/discs - must both be brake pads or brake discs
                    if (partNameLower.includes('brake pad') && desc.includes('brake pad')) return true;
                    if (partNameLower.includes('brake disc') && desc.includes('brake disc')) return true;
                    // DON'T match different components just because they share a word
                    return false;
                  });
                  const isAdded = addedParts[partKey] || alreadyInJob;
                  
                  // Don't render if already in job
                  if (alreadyInJob) return null;
                  
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-2 border rounded text-sm ${
                        isAdded ? 'bg-green-50 border-green-200' : 'bg-white hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {part.is_required ? (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px]">Required</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Optional</Badge>
                        )}
                        <div>
                          <span className="font-medium text-slate-700">{part.name}</span>
                          {part.quantity > 1 && (
                            <span className="text-slate-500 ml-1">x{part.quantity}</span>
                          )}
                          {part.description && (
                            <p className="text-xs text-slate-500">{part.description}</p>
                          )}
                        </div>
                      </div>
                      {isAdded ? (
                        <Badge className="bg-green-600 text-white text-[10px]">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Added
                        </Badge>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          onClick={() => {
                            if (onAddPart) {
                              onAddPart(part, jobGroup.id);
                              setAddedParts(prev => ({ ...prev, [partKey]: true }));
                              toast.success(`Added "${part.name}" to parts`);
                            }
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Suggested Labour from Service Mapping */}
          {serviceMapping.suggested_labour && serviceMapping.suggested_labour.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-slate-600" />
                <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">Suggested Labour</span>
              </div>
              <div className="space-y-1.5">
                {serviceMapping.suggested_labour.map((labour, idx) => {
                  const labourKey = `labour-${idx}`;
                  const isAdded = addedLabour[labourKey];
                  return (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-2 border rounded text-sm ${
                        isAdded ? 'bg-green-50 border-green-200' : 'bg-white hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {labour.is_required ? (
                          <Badge className="bg-purple-100 text-purple-700 text-[10px]">Required</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Optional</Badge>
                        )}
                        <div>
                          <span className="font-medium text-slate-700">{labour.description}</span>
                          {labour.hours && (
                            <p className="text-xs text-slate-500">{labour.hours} hours</p>
                          )}
                        </div>
                      </div>
                      {isAdded ? (
                        <Badge className="bg-green-600 text-white text-[10px]">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Added
                        </Badge>
                      ) : (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs text-purple-700 border-purple-300 hover:bg-purple-50"
                          onClick={() => {
                            if (onAddLabour) {
                              onAddLabour(labour, jobGroup.id);
                              setAddedLabour(prev => ({ ...prev, [labourKey]: true }));
                              toast.success(`Added "${labour.description}" to labour`);
                            }
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Generic mapping note */}
          {isGeneric && (
            <p className="text-xs text-slate-400 pt-2 border-t">
              This is a Generic Default Mapping. Admins can create specific mappings via the Admin panel.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!ruleData?.rule) {
    return null;
  }

  const { rule, suggestions } = ruleData;
  const jobGuide = rule.job_guide || [];
  const isDraft = rule.status === 'draft_needs_review';

  // Organize suggestions by type
  const requiredItems = suggestions?.required || [];
  const suggestedItems = suggestions?.suggested || [];
  const conditionalItems = suggestions?.conditional || [];

  const hasAnySuggestions = requiredItems.length > 0 || suggestedItems.length > 0 || conditionalItems.length > 0;

  return (
    <>
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-white" data-testid={`job-guide-panel-${jobGroup.id}`}>
        <CardHeader className="py-3 px-4 border-b border-blue-100">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-blue-600" />
            <CardTitle className="text-sm font-semibold text-blue-900">
              Job Guide & Suggested Items
            </CardTitle>
            {isDraft && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                Mapping needed
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-4 space-y-4">
          {/* Job Guide Steps */}
          {jobGuide.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <ListChecks className="w-4 h-4 text-slate-600" />
                <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">Job Guide</span>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <ol className="space-y-2">
                  {jobGuide.map((step, idx) => (
                    <li 
                      key={idx} 
                      className={`flex items-start gap-2 text-sm ${step.is_warning ? 'text-amber-700' : 'text-slate-700'}`}
                    >
                      <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                        step.is_warning ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {step.is_warning ? '!' : idx + 1}
                      </span>
                      <span>{step.text}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Suggestions */}
          {hasAnySuggestions && (
            <div className="space-y-3">
              {/* Required Items */}
              {requiredItems.length > 0 && (
                <SuggestionSection
                  title="Included / Required"
                  items={requiredItems}
                  type="required"
                  onApply={handleApplyRule}
                  applying={applying}
                  isRuleApplied={isRuleApplied}
                  jobGroup={jobGroup}
                />
              )}

              {/* Suggested Items */}
              {suggestedItems.length > 0 && (
                <SuggestionSection
                  title="Recommended"
                  items={suggestedItems}
                  type="suggested"
                  onApply={handleApplyRule}
                  applying={applying}
                  isRuleApplied={isRuleApplied}
                  jobGroup={jobGroup}
                />
              )}

              {/* Conditional Items */}
              {conditionalItems.length > 0 && (
                <SuggestionSection
                  title="If Needed"
                  items={conditionalItems}
                  type="conditional"
                  onApply={handleApplyRule}
                  applying={applying}
                  isRuleApplied={isRuleApplied}
                  jobGroup={jobGroup}
                />
              )}
            </div>
          )}

          {!hasAnySuggestions && !isDraft && jobGuide.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-2">
              No additional suggestions for this job
            </p>
          )}
        </CardContent>
      </Card>

      {/* Side Selection Modal */}
      <Dialog open={showSideModal} onOpenChange={setShowSideModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Side</DialogTitle>
            <DialogDescription>
              Choose which side this work applies to
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {pendingSideSelection?.sideOptions.map((side) => (
              <Button
                key={side}
                variant="outline"
                className="h-16 text-lg font-medium hover:bg-blue-50 hover:border-blue-300"
                onClick={() => handleSideSelect(side)}
              >
                {formatSideLabel(side)}
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSideModal(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * SuggestionSection - Renders a group of suggestions
 */
const SuggestionSection = ({ title, items, type, onApply, applying, isRuleApplied, jobGroup }) => {
  const typeColors = {
    required: 'bg-green-50 border-green-200',
    suggested: 'bg-blue-50 border-blue-200',
    conditional: 'bg-slate-50 border-slate-200'
  };

  const badgeColors = {
    required: 'bg-green-100 text-green-800',
    suggested: 'bg-blue-100 text-blue-800',
    conditional: 'bg-slate-100 text-slate-700'
  };

  // iter183 — strict dedupe matching. Replaces the previous substring
  // matcher that was too greedy: "brake fluid flush".includes("brake fluid")
  // false-positived against a part line literally named "brake fluid",
  // silently hiding the Brake Fluid Flush suggestion the moment any
  // brake-fluid-named part landed (this is what made other chips
  // disappear after "Add Brake Fitting Kit").
  //
  // Match precedence:
  //   1. dedupe_key on the rule's pricing_templates vs dedupe_key /
  //      source_rule_id / template_id on existing lines — deterministic,
  //      authored by the rule library.
  //   2. Exact lowercase canonical-name equality with line.description
  //      (no substring).
  // Anything else: NOT a match — show the suggestion.
  const isAlreadyInJob = (item) => {
    const ruleDedupeKeys = new Set([
      ...((item.dedupe_keys || []).map((k) => (k || "").trim().toLowerCase())),
      ...((item.pricing_templates || [])
        .map((pt) => (pt?.dedupe_key || "").trim().toLowerCase())),
    ].filter(Boolean));
    const ruleId = (item.rule_id || "").trim();
    const exactName = (item.name || "").trim().toLowerCase();
    if (!ruleDedupeKeys.size && !ruleId && !exactName) return false;

    const lines = [
      ...(jobGroup?.parts_lines || []),
      ...(jobGroup?.labour_lines || []),
      ...(jobGroup?.charges || []),
    ];

    return lines.some((ln) => {
      // 1. dedupe_key match — authoritative.
      const lnDedupe = (ln?.dedupe_key || "").trim().toLowerCase();
      if (lnDedupe && ruleDedupeKeys.has(lnDedupe)) return true;
      // 2. rule provenance on the line.
      const lnRuleId =
        (ln?.source_rule_id || ln?.applied_rule_id || ln?.rule_id || "")
          .toString().trim();
      if (lnRuleId && ruleId && lnRuleId === ruleId) return true;
      // 3. Exact canonical-name equality (NO substring — was the bug).
      const desc = (ln?.description || "").trim().toLowerCase();
      if (desc && exactName && desc === exactName) return true;
      return false;
    });
  };

  // Filter out items already in the job
  const visibleItems = items.filter(item => {
    if (isRuleApplied(item.rule_id)) return true; // Keep applied items (shows "Added" badge)
    return !isAlreadyInJob(item);
  });

  if (visibleItems.length === 0) return null;

  return (
    <div className={`rounded-lg border p-3 ${typeColors[type]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Badge className={`text-xs ${badgeColors[type]}`}>
          {title}
        </Badge>
      </div>
      <div className="space-y-2">
        {visibleItems.map((item) => {
          const applied = isRuleApplied(item.rule_id);
          const isApplying = applying[item.rule_id];

          return (
            <div 
              key={item.rule_id}
              className="flex items-center justify-between bg-white rounded-md p-2 border border-slate-100"
              data-testid={`suggestion-item-${item.rule_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{item.name}</p>
                {item.note && (
                  <p className="text-xs text-slate-500 truncate">{item.note}</p>
                )}
              </div>
              <div className="flex-shrink-0 ml-2">
                {applied ? (
                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-300">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Added
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant={type === 'required' ? 'default' : 'outline'}
                    className={`h-7 text-xs ${type === 'required' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => onApply(item.rule_id, item.requires_side_selection, item.side_options)}
                    disabled={isApplying || !item.priceable}
                    data-testid={`apply-suggestion-${item.rule_id}`}
                  >
                    {isApplying ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-3 h-3 mr-1" />
                        {type === 'required' ? 'Include' : 'Add'}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Format side label for display
 */
const formatSideLabel = (side) => {
  const labels = {
    osf: 'OSF (Offside Front)',
    nsf: 'NSF (Nearside Front)',
    osr: 'OSR (Offside Rear)',
    nsr: 'NSR (Nearside Rear)',
    front: 'Front (Both)',
    rear: 'Rear (Both)',
    both: 'All Four',
    left: 'Left Side',
    right: 'Right Side'
  };
  return labels[side?.toLowerCase()] || side?.toUpperCase();
};

export default JobGuidePanel;
